import { ENDPOINT } from '~/constants';

import { type KyRequestOptions } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import {
  type InvoiceProcessFromInvoiceIdsParams,
  type InvoiceProcessFromInvoiceIdsResponse,
} from './types';
import { zodInvoiceProcessFromInvoiceIdsParamsSchema } from './validations';

/**
 * Fetches invoice processes by invoice IDs.
 * @param params - The parameters containing invoice IDs.
 * @returns The invoice processes data or empty array if not found.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_processes_by_invoice_ids_invoice_process_by_invoice_ids_get
 */
export const fetchInvoiceProcessesFromInvoiceIds = async (
  invoiceIds: InvoiceProcessFromInvoiceIdsParams,
  options?: KyRequestOptions,
): Promise<InvoiceProcessFromInvoiceIdsResponse> => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.INVOICE_REPROCESSING.GET_PROCESSES_FROM_INVOICE_IDS(), {
        searchParams: { invoiceId: invoiceIds }, // The API calls the array of UUIDs "invoice_id"
        validate: { searchParams: zodInvoiceProcessFromInvoiceIdsParamsSchema },
        ...options,
      })
      .json<InvoiceProcessFromInvoiceIdsResponse>();

    return response;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching Invoice Processes From Invoice IDs',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 422: {
          errorMessage = i18n.t(
            ($) =>
              $.invoiceReprocessingQueries.fetchInvoiceProcessesFromInvoiceIds
                .error['422'],
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) =>
              $.invoiceReprocessingQueries.fetchInvoiceProcessesFromInvoiceIds
                .error.generic,
            { ns: 'data' },
          );
          break;
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
